import { createContext, type CSSProperties, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, IconButton, Snackbar, Stack, type AlertColor, type SnackbarCloseReason, type SnackbarOrigin, type SxProps, type Theme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { APP_HEADER_HEIGHT_PX, DEFAULT_SNACKBAR_AUTO_HIDE_DURATION_MS } from "@/utils/constants";

type MuiToastVariant = AlertColor | "action";

const ACTION_TOAST_BG_COLOR = "#0288d1";
const TOAST_GROUP_MARGIN_PX = 16;
/** Top-anchored toasts sit below the sticky AppHeader instead of overlapping it - the header's
 * z-index (MUI's default `appBar`) is lower than the toast group's, so without this offset a
 * top-center/left/right toast renders directly on top of the header's buttons/menus. */
const TOAST_TOP_OFFSET_PX = APP_HEADER_HEIGHT_PX + TOAST_GROUP_MARGIN_PX;
const TOAST_STACK_GAP_PX = 8;
const TOAST_Z_INDEX = 1400;

interface MuiToastAction {
  readonly label: string;
  /** Optional - the toast always dismisses on click regardless; only pass this for extra side effects. */
  readonly onClick?: () => void;
}

interface MuiToastOptions {
  readonly variant?: MuiToastVariant;
  readonly autoHideDuration?: number | null;
  /** Shorthand for `autoHideDuration: null` - the toast never auto-hides until the user dismisses
   * it (close icon or action). Kept as its own flag for callers that don't otherwise care about
   * autoHideDuration. */
  readonly persist?: boolean;
  readonly anchorOrigin?: SnackbarOrigin;
  readonly preventDuplicate?: boolean;
  readonly action?: MuiToastAction;
  readonly sx?: SxProps<Theme>;
}

interface ToastInternal {
  id: number;
  message: string;
  variant: MuiToastVariant;
  /** `null` disables MUI Snackbar's auto-hide timer entirely (persistent toast). */
  autoHideDuration: number | null;
  anchorOrigin: SnackbarOrigin;
  action?: MuiToastAction;
  sx?: SxProps<Theme>;
}

interface SnackbarApi {
  enqueue: (message: string, options?: MuiToastOptions) => void;
  close: (id?: number) => void;
}

const SnackbarContext = createContext<SnackbarApi | null>(null);

const useMuiSnackbar = () => {
  const ctx = useContext(SnackbarContext);
  if (!ctx) {
    throw new Error("useMuiSnackbar must be used within MuiSnackbarProvider");
  }

  return ctx;
};

/** `persist: true` and an explicit `autoHideDuration: null` are equivalent - both disable
 * auto-hide. Omitting `autoHideDuration` entirely falls back to the normal default duration. */
function resolveAutoHideDuration(options?: MuiToastOptions): number | null {
  if (options?.persist) {
    return null;
  }

  return options?.autoHideDuration === undefined ? DEFAULT_SNACKBAR_AUTO_HIDE_DURATION_MS : options.autoHideDuration;
}

let globalEnqueue: SnackbarApi["enqueue"] | null = null;
const enqueueMuiToast = (message: string, options?: MuiToastOptions) => {
  globalEnqueue?.(message, options);
};

function groupContainerStyle(anchorOrigin: SnackbarOrigin): CSSProperties {
  const { vertical, horizontal } = anchorOrigin;

  let alignItems: CSSProperties["alignItems"] = "center";
  let horizontalStyle: CSSProperties = { left: "50%", transform: "translateX(-50%)" };
  if (horizontal === "left") {
    alignItems = "flex-start";
    horizontalStyle = { left: TOAST_GROUP_MARGIN_PX };
  } else if (horizontal === "right") {
    alignItems = "flex-end";
    horizontalStyle = { right: TOAST_GROUP_MARGIN_PX };
  }

  return {
    position: "fixed",
    zIndex: TOAST_Z_INDEX,
    display: "flex",
    flexDirection: "column",
    gap: TOAST_STACK_GAP_PX,
    alignItems,
    pointerEvents: "none",
    ...(vertical === "top" ? { top: TOAST_TOP_OFFSET_PX } : { bottom: TOAST_GROUP_MARGIN_PX }),
    ...horizontalStyle
  };
}

const MuiSnackbarProvider = ({ children }: { readonly children: ReactNode }) => {
  const [toasts, setToasts] = useState<ToastInternal[]>([]);
  const nextIdRef = useRef(1);

  const close = useCallback((id?: number) => {
    if (typeof id === "number") {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      return;
    }

    setToasts([]);
  }, []);

  const enqueue = useCallback((message: string, options?: MuiToastOptions) => {
    const preventDuplicate = options?.preventDuplicate ?? false;

    const toast: ToastInternal = {
      id: nextIdRef.current++,
      message,
      variant: options?.variant ?? "info",
      // `??` would treat an explicit `null` (persist until dismissed) the same as "not passed" -
      // check for `undefined` specifically so callers can opt out of the default auto-hide.
      autoHideDuration: resolveAutoHideDuration(options),
      anchorOrigin: options?.anchorOrigin ?? { vertical: "bottom", horizontal: "left" },
      action: options?.action,
      sx: options?.sx
    };

    setToasts((prev) => {
      if (preventDuplicate && prev.some((t) => t.message === toast.message && t.variant === toast.variant)) {
        return prev;
      }

      const maxSnack = 5;
      const next = [toast, ...prev];
      return next.length > maxSnack ? next.slice(0, maxSnack) : next;
    });
  }, []);

  useEffect(() => {
    globalEnqueue = enqueue;
    return () => {
      globalEnqueue = null;
    };
  }, [enqueue]);

  const api = useMemo<SnackbarApi>(() => ({ enqueue, close }), [enqueue, close]);

  const groups = new Map<string, { anchorOrigin: SnackbarOrigin; items: ToastInternal[] }>();
  for (const t of toasts) {
    const { vertical, horizontal } = t.anchorOrigin;
    const key = `${vertical}-${horizontal}`;
    const group = groups.get(key) ?? { anchorOrigin: t.anchorOrigin, items: [] };
    group.items.push(t);
    groups.set(key, group);
  }

  return (
    <SnackbarContext.Provider value={api}>
      {children}
      {Array.from(groups.entries()).map(([key, group]) => {
        const orderedItems = group.anchorOrigin.vertical === "bottom" ? group.items.toReversed() : group.items;

        return (
          <div key={key} style={groupContainerStyle(group.anchorOrigin)}>
            {orderedItems.map((t) => {
              const isAction = t.variant === "action";
              const severity: AlertColor = t.variant === "action" ? "info" : t.variant;

              const handleClose = (_event: unknown, reason?: SnackbarCloseReason) => {
                if (reason === "clickaway") {
                  return;
                }

                close(t.id);
              };

              return (
                <Snackbar
                  key={t.id}
                  open
                  anchorOrigin={group.anchorOrigin}
                  autoHideDuration={t.autoHideDuration}
                  {...(t.autoHideDuration !== null ? { resumeHideDuration: t.autoHideDuration } : {})}
                  onClose={handleClose}
                  style={{ position: "static", pointerEvents: "auto" }}
                >
                  <Alert
                    variant="filled"
                    severity={severity}
                    icon={isAction ? <InfoOutlinedIcon fontSize="small" sx={{ color: "#fff" }} /> : undefined}
                    onClose={() => close(t.id)}
                    sx={{
                      width: "100%",
                      ...(isAction ? { bgcolor: ACTION_TOAST_BG_COLOR, color: "#fff" } : {}),
                      ...t.sx
                    }}
                    {...(t.action
                      ? {
                          action: (
                            <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                              <Button
                                color="inherit"
                                size="small"
                                onClick={() => {
                                  t.action?.onClick?.();
                                  close(t.id);
                                }}
                                sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                              >
                                {t.action.label}
                              </Button>
                              <IconButton size="small" color="inherit" aria-label="Close" onClick={() => close(t.id)}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          )
                        }
                      : {})}
                  >
                    {t.message}
                  </Alert>
                </Snackbar>
              );
            })}
          </div>
        );
      })}
    </SnackbarContext.Provider>
  );
};

export { MuiSnackbarProvider, useMuiSnackbar, enqueueMuiToast };
export type { MuiToastOptions, MuiToastAction };
